import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { getTranslation } from '../utils/translations';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════
   BLOG POST TILE TRANSLATIONS (5 languages)
   ═══════════════════════════════════════════ */
const POST_TRANSLATIONS = {
  'a-machine-that-shouldnt-exist': {
    en: { title: "A Machine That Shouldn't Exist", excerpt: "It began as a Porsche 930. It became something else entirely.", category: "Vehicle Spotlights" },
    es: { title: "Una Máquina Que No Debería Existir", excerpt: "Comenzó como un Porsche 930. Se convirtió en algo completamente diferente.", category: "Vehículos Destacados" },
    pt: { title: "Uma Máquina Que Não Deveria Existir", excerpt: "Começou como um Porsche 930. Tornou-se algo completamente diferente.", category: "Destaques de Veículos" },
    fr: { title: "Une Machine Qui Ne Devrait Pas Exister", excerpt: "Tout a commencé avec une Porsche 930. Elle est devenue tout autre chose.", category: "Véhicules en Vedette" },
    it: { title: "Una Macchina Che Non Dovrebbe Esistere", excerpt: "È nata come una Porsche 930. È diventata qualcosa di completamente diverso.", category: "Vetture in Evidenza" },
  },
  'the-30-who-were-right': {
    en: { title: "The 30 Who Were Right", excerpt: "Only 30 buyers chose the manual 599. This is what happened when one owner chose it again.", category: "Vehicle Spotlights" },
    es: { title: "Los 30 Que Tenían Razón", excerpt: "Solo 30 compradores eligieron el 599 manual. Esto es lo que pasó cuando un propietario lo eligió de nuevo.", category: "Vehículos Destacados" },
    pt: { title: "Os 30 Que Estavam Certos", excerpt: "Apenas 30 compradores escolheram o 599 manual. Isto é o que aconteceu quando um proprietário o escolheu novamente.", category: "Destaques de Veículos" },
    fr: { title: "Les 30 Qui Avaient Raison", excerpt: "Seuls 30 acheteurs ont choisi la 599 manuelle. Voici ce qui s'est passé quand un propriétaire l'a choisie à nouveau.", category: "Véhicules en Vedette" },
    it: { title: "I 30 Che Avevano Ragione", excerpt: "Solo 30 acquirenti scelsero la 599 manuale. Ecco cosa è successo quando un proprietario l'ha scelta di nuovo.", category: "Vetture in Evidenza" },
  },
  'the-forever-young-ferrari-sf90-spider': {
    en: { title: 'The "Forever Young" Ferrari SF90 Spider', excerpt: "A short film.", category: "Film" },
    es: { title: 'El Ferrari SF90 Spider "Forever Young"', excerpt: "Un cortometraje.", category: "Film" },
    pt: { title: 'A Ferrari SF90 Spider "Forever Young"', excerpt: "Um curta-metragem.", category: "Film" },
    fr: { title: 'La Ferrari SF90 Spider "Forever Young"', excerpt: "Un court métrage.", category: "Film" },
    it: { title: 'La Ferrari SF90 Spider "Forever Young"', excerpt: "Un cortometraggio.", category: "Film" },
  },
};

/* Slug key from title (fallback matching) */
const titleToSlugKey = (title) => {
  const map = {
    "A Machine That Shouldn't Exist": 'a-machine-that-shouldnt-exist',
    "The 30 Who Were Right": 'the-30-who-were-right',
    'The "Forever Young" Ferrari SF90 Spider': 'the-forever-young-ferrari-sf90-spider',
  };
  return map[title] || null;
};

/* Translate "X min read" */
const translateReadTime = (readTime, lang) => {
  if (!readTime || lang === 'en') return readTime;
  const m = readTime.match(/(\d+)\s*min/);
  if (!m) return readTime;
  const n = m[1];
  const labels = { es: 'min de lectura', pt: 'min de leitura', fr: 'min de lecture', it: 'min di lettura' };
  return `${n} ${labels[lang] || 'min read'}`;
};

/* Translate date strings like "Jan 7, 2026" into all 5 languages */
const MONTH_MAP = {
  Jan: { es: 'ene', pt: 'jan', fr: 'janv.', it: 'gen' },
  Feb: { es: 'feb', pt: 'fev', fr: 'fév.', it: 'feb' },
  Mar: { es: 'mar', pt: 'mar', fr: 'mars', it: 'mar' },
  Apr: { es: 'abr', pt: 'abr', fr: 'avr.', it: 'apr' },
  May: { es: 'may', pt: 'mai', fr: 'mai', it: 'mag' },
  Jun: { es: 'jun', pt: 'jun', fr: 'juin', it: 'giu' },
  Jul: { es: 'jul', pt: 'jul', fr: 'juil.', it: 'lug' },
  Aug: { es: 'ago', pt: 'ago', fr: 'août', it: 'ago' },
  Sep: { es: 'sept', pt: 'set', fr: 'sept.', it: 'set' },
  Oct: { es: 'oct', pt: 'out', fr: 'oct.', it: 'ott' },
  Nov: { es: 'nov', pt: 'nov', fr: 'nov.', it: 'nov' },
  Dec: { es: 'dic', pt: 'dez', fr: 'déc.', it: 'dic' },
};
const translateDate = (dateStr, lang) => {
  if (!dateStr || lang === 'en') return dateStr;
  const m = dateStr.match(/^(\w{3})\s+(\d{1,2}),?\s+(\d{4})$/);
  if (!m) return dateStr;
  const [, mon, day, year] = m;
  const locMonth = MONTH_MAP[mon]?.[lang] || mon;
  return `${day} ${locMonth} ${year}`;
};

const Blog = () => {
  useDocumentMeta({
    title: 'Revs Tales | Stories Worth Carrying Forward',
    description: 'Read Revs Tales, the journal of Revs Vibes featuring collector car stories, heritage, design, memory, and meaning.',
    canonical: 'https://www.revsvibes.com/revs-tales',
    image: 'https://customer-assets.emergentagent.com/job_b3960aa7-330d-4b96-81c0-018bec21e519/artifacts/dh8wksrr_image.png',
  });
  const { language } = useLanguage();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/blog`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="relative h-72 sm:h-96 page-header bg-black text-white overflow-hidden clickable-card">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_f184c80b-9543-4a62-923b-8fe110e27c5d/artifacts/pvvhpk2q_1000442%20%281%29.jpg"
            alt="Blog"
            className="w-full h-full object-cover opacity-40 blog-header-img"
            style={{ objectPosition: 'center 65%' }}
          />
        </div>
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center px-4 max-w-3xl">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-semibold mb-4">{getTranslation(language, 'blog.title')}</h1>
            <p className="text-base sm:text-lg text-gray-300">
              {getTranslation(language, 'blog.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Manifesto intro */}
      <div className="py-14 sm:py-16 bg-white text-center">
        <p className="text-[11px] sm:text-xs tracking-[0.24em] font-light" style={{ color: '#a8adb5' }}>
          {language === 'en' ? 'Each car carries a story. We simply continue it.' :
           language === 'es' ? 'Cada coche lleva una historia. Nosotros simplemente la continuamos.' :
           language === 'pt' ? 'Cada carro carrega uma hist\u00f3ria. N\u00f3s apenas a continuamos.' :
           language === 'fr' ? 'Chaque voiture porte une histoire. Nous la continuons simplement.' :
           'Ogni auto porta una storia. Noi semplicemente la continuiamo.'}
        </p>
      </div>

      {/* Quote Section */}
      <section className="py-32 relative blog-quote-section overflow-hidden">
        <div className="absolute inset-0 blog-quote-img">
          <picture>
            <source
              media="(max-width: 767px) and (orientation: portrait)"
              srcSet="https://customer-assets.emergentagent.com/job_f184c80b-9543-4a62-923b-8fe110e27c5d/artifacts/6l8kq2l5_image.png"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_f184c80b-9543-4a62-923b-8fe110e27c5d/artifacts/sedcdoh5_wp14933470-jaguar-d-type-wallpapers.webp"
              alt="Jaguar D-Type"
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center 70%' }}
            />
          </picture>
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <blockquote className="text-3xl md:text-4xl font-light italic mb-6 text-white/90">
            "{getTranslation(language, 'blogQuote.text')}"
          </blockquote>
          <cite className="text-white/50 not-italic">— {getTranslation(language, 'blogQuote.author')}</cite>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-gray-400" size={32} />
            </div>
          ) : posts.length === 0 ? (
            <p className="text-center text-gray-400 py-20">No articles yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => {
                const slugKey = post.slug || titleToSlugKey(post.title);
                const tr = slugKey && POST_TRANSLATIONS[slugKey]?.[language];
                const title = tr?.title || post.title;
                const excerpt = tr?.excerpt || post.excerpt;
                const category = tr?.category || post.category;
                const readTime = translateReadTime(post.read_time, language);
                const displayDate = translateDate(post.date, language);
                const imgStyle = 'w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500';
                const imgContainerBg = '';
                return (
                <Link key={post.id} to={post.slug ? `/tales/${post.slug}` : '#'}>
                <Card className="clickable-card group cursor-pointer overflow-hidden border-none shadow-sm hover:shadow-md transition-all duration-300" data-testid={`blog-post-${post.id}`}>
                  <div className={`relative aspect-[3/2] overflow-hidden ${imgContainerBg}`}>
                    <img
                      src={post.image}
                      alt={title}
                      className={imgStyle}
                    />
                    <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs">
                      {category}
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-1 text-sm text-gray-400 mb-3">
                      <span>{displayDate}</span>
                      {readTime && (
                        <>
                          <span style={{ margin: '0 6px' }}>·</span>
                          <span>{readTime}</span>
                        </>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold mb-3 text-black group-hover:text-gray-600 transition-colors">
                      {title}
                    </h3>
                    <p className="text-gray-500 text-sm mb-4 line-clamp-3">{excerpt}</p>
                    <span className="text-[13px] font-medium text-gray-400 group-hover:text-gray-600">
                      {post.category === 'Film'
                        ? (language === 'en' ? 'Watch' : language === 'es' ? 'Ver' : language === 'pt' ? 'Assistir' : language === 'fr' ? 'Regarder' : 'Guarda')
                        : getTranslation(language, 'blog.readMore')} →
                    </span>
                  </CardContent>
                </Card>
                </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Founder imprint */}
      <div className="py-14 sm:py-16 bg-white text-center">
        <p className="text-[11px] sm:text-xs tracking-[0.24em] font-light" style={{ color: '#a8adb5' }}>
          {language === 'en' ? 'Some stories are driven. Some are carried forward.' :
           language === 'es' ? 'Algunas historias son conducidas. Algunas se llevan hacia adelante.' :
           language === 'pt' ? 'Algumas hist\u00f3rias s\u00e3o conduzidas. Algumas s\u00e3o levadas adiante.' :
           language === 'fr' ? 'Certaines histoires sont conduites. Certaines sont perp\u00e9tu\u00e9es.' :
           'Alcune storie sono guidate. Altre vengono portate avanti.'}
        </p>
      </div>

      {/* Newsletter Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-4">
            {language === 'en' ? 'Stay Updated' :
             language === 'es' ? 'Mantente Actualizado' :
             language === 'pt' ? 'Mantenha-se Atualizado' :
             language === 'fr' ? 'Restez Informé' :
             'Rimani Aggiornato'}
          </h2>
          <p className="text-gray-500 mb-8">
            {language === 'en' ? 'Subscribe to our newsletter for the latest arrivals, market insights, and automotive stories.' :
             language === 'es' ? 'Suscríbete a nuestro boletín para las últimas llegadas, perspectivas del mercado e historias automotrices.' :
             language === 'pt' ? 'Inscreva-se em nossa newsletter para as últimas chegadas, insights de mercado e histórias automotivas.' :
             language === 'fr' ? 'Abonnez-vous à notre newsletter pour les dernières arrivées, les perspectives du marché et les histoires automobiles.' :
             'Iscriviti alla nostra newsletter per gli ultimi arrivi, approfondimenti di mercato e storie automobilistiche.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              placeholder={language === 'en' ? 'Enter your email' :
                          language === 'es' ? 'Ingresa tu correo' :
                          language === 'pt' ? 'Digite seu e-mail' :
                          language === 'fr' ? 'Entrez votre e-mail' :
                          'Inserisci la tua email'}
              className="flex-1 px-4 py-3 border border-gray-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
              data-testid="newsletter-email-input"
            />
            <button 
              className="px-6 py-3 bg-black text-white rounded-sm hover:bg-gray-800 transition-colors flex items-center justify-center"
              disabled={subscribing}
              data-testid="newsletter-subscribe-btn"
              onClick={async () => {
                if (!newsletterEmail) return;
                setSubscribing(true);
                try {
                  const res = await fetch(`${API_URL}/api/newsletter`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: newsletterEmail })
                  });
                  if (res.status === 409) {
                    toast({ title: language === 'en' ? 'Already Subscribed' : 'Ya suscrito', description: language === 'en' ? 'This email is already subscribed.' : 'Este correo ya está suscrito.' });
                  } else if (!res.ok) {
                    throw new Error('Failed');
                  } else {
                    toast({ title: language === 'en' ? 'Subscribed!' : '¡Suscrito!', description: language === 'en' ? 'Welcome to the Revs Vibes newsletter.' : 'Bienvenido al boletín de Revs Vibes.' });
                    setNewsletterEmail('');
                  }
                } catch {
                  toast({ title: 'Error', description: language === 'en' ? 'Something went wrong. Please try again.' : 'Algo salió mal.', variant: 'destructive' });
                } finally {
                  setSubscribing(false);
                }
              }}
            >
              {subscribing && <Loader2 className="animate-spin mr-2" size={16} />}
              {language === 'en' ? 'Subscribe' :
               language === 'es' ? 'Suscribirse' :
               language === 'pt' ? 'Inscrever-se' :
               language === 'fr' ? 'S\'abonner' :
               'Iscriviti'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
